import Debug from '../utils/Debug.js';

/**
 * Path following component for characters.
 * Moves a character along a path of waypoints with collision avoidance.
 */
export default class PathFollower {
  // Default walk speed (matches CharacterController.WALK_SPEED)
  static WALK_SPEED = 5;

  // Stuck detection constants
  static STUCK_TIME_THRESHOLD = 2.0; // Seconds without progress
  static STUCK_DISTANCE_THRESHOLD = 0.2; // Minimum progress distance

  // Collision avoidance constants
  static LOOK_AHEAD_DISTANCE = 1.0; // Units ahead to check for obstacles
  static AVOIDANCE_STRENGTH = 0.3; // How much to steer away from obstacles (reduced)

  // Direction smoothing to prevent oscillation
  static DIRECTION_SMOOTHING = 0.15; // How fast direction changes (0=instant, 1=never)

  // Edge clamping to prevent falling off surfaces during movement
  static EDGE_CLAMP_DISTANCE = 0.5; // Stay this far from surface edges

  // Character dimensions for collision queries
  static CHARACTER_WIDTH = 0.9;
  static CHARACTER_HEIGHT = 2.0;
  static CHARACTER_DEPTH = 0.9;

  /**
   * Create a path follower
   * @param {Character} character - The character to move
   * @param {Object} options - Configuration options
   */
  constructor(character, options = {}) {
    this.character = character;
    this.path = null;
    this.waypointIndex = 0;

    // How close to waypoint before advancing to next
    this.arrivalThreshold = options.arrivalThreshold || 0.5;

    // Movement speed
    this.speed = options.speed || PathFollower.WALK_SPEED;

    // State flags
    this.isFollowing = false;

    // Collision system for obstacle queries
    this.collisionSystem = options.collisionSystem || null;

    // Callback for path completion
    this.onComplete = options.onComplete || null;

    // Callback for path cancelled
    this.onCancelled = options.onCancelled || null;

    // Callback for repath request (when stuck)
    this.onRepathRequest = options.onRepathRequest || null;

    // Stuck detection state
    this._lastPosition = { x: 0, z: 0 };
    this._stuckTimer = 0;
    this._lastProgressTime = 0;

    // Fall detection state
    this._lastY = null;

    // Direction smoothing state (prevents oscillation)
    this._smoothedDirX = 0;
    this._smoothedDirZ = 0;
  }

  /**
   * Set the collision system for obstacle queries
   * @param {CharacterCollisionSystem} collisionSystem
   */
  setCollisionSystem(collisionSystem) {
    this.collisionSystem = collisionSystem;
  }

  /**
   * Set a new path to follow
   * @param {Array} waypoints - Array of {x, y, z, type?} positions
   */
  setPath(waypoints) {
    if (!waypoints || waypoints.length === 0) {
      this.stop();
      return;
    }

    Debug.log('PathFollower', 'setPath received', waypoints.length, 'waypoints');
    for (let i = 0; i < Math.min(waypoints.length, 5); i++) {
      const wp = waypoints[i];
      Debug.log('PathFollower', '  RECEIVED [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2));
    }

    const wasFollowing = this.isFollowing;
    this.path = waypoints;
    this.waypointIndex = 0;
    this.isFollowing = true;

    // Reset stuck detection
    this._lastPosition = { x: this.character.x, z: this.character.z };
    this._stuckTimer = 0;

    // Reset fall detection
    this._lastY = this.character.y;

    // Preserve direction smoothing across repaths (e.g. chase repathing)
    // to prevent jittery direction snaps. Only reset when starting fresh.
    if (!wasFollowing) {
      this._smoothedDirX = 0;
      this._smoothedDirZ = 0;
    }
  }

  /**
   * Stop following the current path
   * @param {boolean} cancelled - Whether this was user-initiated cancellation
   */
  stop(cancelled = false) {
    const wasFollowing = this.isFollowing;

    this.isFollowing = false;
    this.path = null;
    this.waypointIndex = 0;
    this._stuckTimer = 0;
    this._smoothedDirX = 0;
    this._smoothedDirZ = 0;
    this._lastY = null;

    // Clear character velocity
    if (this.character) {
      this.character.velocity.x = 0;
      this.character.velocity.z = 0;
      this.character.setMovementState(false, false);
    }

    // Trigger callbacks
    if (wasFollowing) {
      if (cancelled && this.onCancelled) {
        this.onCancelled();
      }
    }
  }

  /**
   * Check if path following is complete
   * @returns {boolean}
   */
  isComplete() {
    return !this.isFollowing;
  }

  /**
   * Get current target waypoint
   * @returns {Object|null} Current waypoint {x, y, z, type?} or null
   */
  getCurrentWaypoint() {
    if (!this.path || this.waypointIndex >= this.path.length) {
      return null;
    }
    return this.path[this.waypointIndex];
  }

  /**
   * Get final destination
   * @returns {Object|null} Final waypoint {x, y, z} or null
   */
  getDestination() {
    if (!this.path || this.path.length === 0) {
      return null;
    }
    return this.path[this.path.length - 1];
  }

  /**
   * Update path following (call each frame)
   * @param {number} deltaTime - Time since last update in seconds
   * @returns {Object|null} Movement direction {x, z} or null if not following
   */
  update(deltaTime) {
    if (!this.isFollowing || !this.path) {
      return null;
    }

    const waypoint = this.getCurrentWaypoint();
    if (!waypoint) {
      this._completeFollowing();
      return null;
    }

    // Check for stuck condition
    if (this._checkStuck(deltaTime)) {
      this._handleStuck();
      return null;
    }

    // Check for unexpected fall (character Y dropped significantly)
    if (this._checkUnexpectedFall(waypoint)) {
      Debug.warn('PathFollower', 'Unexpected fall detected, requesting repath');
      this._handleStuck();
      return null;
    }

    // Calculate direction to waypoint
    let dx = waypoint.x - this.character.x;
    let dz = waypoint.z - this.character.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Check if arrived at waypoint
    if (distance < this.arrivalThreshold) {
      // Handle special waypoint types
      if (waypoint.type === 'jump' && this.character.jump) {
        // Trigger jump for jump waypoints
        this.character.jump();
      }

      this.waypointIndex++;

      // Check if reached final waypoint
      if (this.waypointIndex >= this.path.length) {
        this._completeFollowing();
        return null;
      }

      // Continue to next waypoint
      return this.update(deltaTime);
    }

    // Normalize direction to waypoint
    let targetDirX = dx / distance;
    let targetDirZ = dz / distance;

    // Apply collision avoidance (perpendicular steering only to prevent backward movement)
    if (this.collisionSystem) {
      const avoidance = this._getAvoidanceDirection(targetDirX, targetDirZ);
      if (avoidance) {
        // Only apply the component of avoidance that is perpendicular to target direction
        // This prevents the character from being pushed backward
        const dot = avoidance.x * targetDirX + avoidance.z * targetDirZ;

        // Remove the parallel component (keep only perpendicular steering)
        let perpX = avoidance.x - dot * targetDirX;
        let perpZ = avoidance.z - dot * targetDirZ;

        // Apply perpendicular avoidance
        targetDirX += perpX * PathFollower.AVOIDANCE_STRENGTH;
        targetDirZ += perpZ * PathFollower.AVOIDANCE_STRENGTH;

        // Renormalize
        const len = Math.sqrt(targetDirX * targetDirX + targetDirZ * targetDirZ);
        if (len > 0) {
          targetDirX /= len;
          targetDirZ /= len;
        }
      }
    }

    // Smooth direction changes to prevent oscillation
    // Reduce smoothing when close to waypoint to prevent drift/overshooting near edges
    let smoothing = PathFollower.DIRECTION_SMOOTHING;
    if (distance < 2.0) {
      // Reduce smoothing when close to waypoint for more precise movement
      smoothing = smoothing * (distance / 2.0);
    }
    if (distance < 1.0) {
      // Very close - use direct movement with minimal smoothing
      smoothing = 0.05;
    }

    if (this._smoothedDirX === 0 && this._smoothedDirZ === 0) {
      // Initialize smoothed direction
      this._smoothedDirX = targetDirX;
      this._smoothedDirZ = targetDirZ;
    } else {
      // Lerp toward target direction
      this._smoothedDirX += (targetDirX - this._smoothedDirX) * (1 - smoothing);
      this._smoothedDirZ += (targetDirZ - this._smoothedDirZ) * (1 - smoothing);

      // Renormalize smoothed direction
      const smoothLen = Math.sqrt(this._smoothedDirX * this._smoothedDirX + this._smoothedDirZ * this._smoothedDirZ);
      if (smoothLen > 0) {
        this._smoothedDirX /= smoothLen;
        this._smoothedDirZ /= smoothLen;
      }
    }

    // Use smoothed direction for movement
    let dirX = this._smoothedDirX;
    let dirZ = this._smoothedDirZ;

    // DRIFT CORRECTION: Prevent the character from drifting too far off the direct path
    // This is critical for narrow platforms where accumulated smoothing drift can cause falls
    const MAX_DRIFT = 0.5; // Maximum allowed perpendicular distance from direct path (reduced for safety)

    // Calculate perpendicular distance from character to the direct line to waypoint
    // Using the formula for distance from point to line
    const toWaypointX = waypoint.x - this.character.x;
    const toWaypointZ = waypoint.z - this.character.z;

    // Cross product gives us the perpendicular component of our movement direction
    const crossProduct = dirX * toWaypointZ - dirZ * toWaypointX;
    const perpDist = Math.abs(crossProduct) / Math.max(distance, 0.1);

    if (perpDist > MAX_DRIFT && distance > 1.0) {
      // We're drifting too far - use direct path to waypoint (no blending)
      // This is aggressive but necessary to prevent edge falls on narrow platforms
      const directDirX = toWaypointX / distance;
      const directDirZ = toWaypointZ / distance;

      dirX = directDirX;
      dirZ = directDirZ;

      // Reset smoothed direction to direct to prevent oscillation
      this._smoothedDirX = dirX;
      this._smoothedDirZ = dirZ;

      Debug.log('PathFollower', 'DRIFT CORRECTION: perpDist=' + perpDist.toFixed(2) + ' > MAX=' + MAX_DRIFT);
    }

    // Calculate velocity
    let velX = dirX * this.speed;
    let velZ = dirZ * this.speed;

    // Clamp velocity to prevent walking off surface edges
    if (this.collisionSystem) {
      const clampedVel = this._clampVelocityToSurface(velX, velZ, deltaTime);
      velX = clampedVel.x;
      velZ = clampedVel.z;
    }

    // Apply velocity to character
    this.character.velocity.x = velX;
    this.character.velocity.z = velZ;

    Debug.log('PathFollower', 'Moving toward waypoint',
              this.waypointIndex, 'at', waypoint.x.toFixed(1), waypoint.z.toFixed(1),
              'from', this.character.x.toFixed(1), this.character.z.toFixed(1),
              'vel=', (dirX * this.speed).toFixed(2), (dirZ * this.speed).toFixed(2),
              'dist=', distance.toFixed(2));

    // Update character facing from smoothed direction
    const radians = Math.atan2(dirZ, dirX);
    const degrees = radians * (180 / Math.PI);
    this.character.facingAngle = ((degrees % 360) + 360) % 360;

    // Update animation state (walking)
    this.character.setMovementState(true, false);

    // Update last position for stuck detection
    this._lastPosition = { x: this.character.x, z: this.character.z };

    return { x: dirX, z: dirZ };
  }

  /**
   * Check if character is stuck
   * @private
   */
  _checkStuck(deltaTime) {
    const dx = this.character.x - this._lastPosition.x;
    const dz = this.character.z - this._lastPosition.z;
    const moved = Math.sqrt(dx * dx + dz * dz);
    const threshold = PathFollower.STUCK_DISTANCE_THRESHOLD * deltaTime;

    if (moved < threshold) {
      this._stuckTimer += deltaTime;
      if (this._stuckTimer > 0.5) {
        Debug.log('PathFollower', 'Stuck check: moved=', moved.toFixed(4),
                  'threshold=', threshold.toFixed(4),
                  'timer=', this._stuckTimer.toFixed(1),
                  'pos=', this.character.x.toFixed(1), this.character.z.toFixed(1));
      }
      return this._stuckTimer >= PathFollower.STUCK_TIME_THRESHOLD;
    } else {
      this._stuckTimer = 0;
      return false;
    }
  }

  /**
   * Check if character fell unexpectedly (not a planned fall transition)
   * @private
   */
  _checkUnexpectedFall(waypoint) {
    if (this._lastY === null) {
      this._lastY = this.character.y;
      return false;
    }

    const yDrop = this._lastY - this.character.y;

    // Update lastY for next frame
    const currentY = this.character.y;

    // Only check if we dropped significantly (more than 0.5 units)
    if (yDrop > 0.5) {
      // Check if this was a planned fall/jump
      const isPlannedTransition = waypoint && (
        waypoint.type === 'fall' ||
        waypoint.type === 'jump' ||
        waypoint.y < this._lastY - 0.3  // Waypoint is below us
      );

      this._lastY = currentY;

      if (!isPlannedTransition) {
        return true; // Unexpected fall!
      }
    }

    this._lastY = currentY;
    return false;
  }

  /**
   * Handle stuck condition
   * @private
   */
  _handleStuck() {
    Debug.warn('PathFollower', 'Character is stuck, requesting repath');

    // Reset stuck timer
    this._stuckTimer = 0;

    // Request repath if callback available
    if (this.onRepathRequest) {
      const destination = this.getDestination();
      if (destination) {
        this.onRepathRequest(destination);
      }
    } else {
      // No repath callback - just stop
      this.stop(true);
    }
  }

  /**
   * Get avoidance direction to steer away from obstacles
   * @private
   */
  _getAvoidanceDirection(dirX, dirZ) {
    if (!this.collisionSystem) return null;

    const charX = this.character.x;
    const charZ = this.character.z;
    const charY = this.character.y;

    const lookAhead = PathFollower.LOOK_AHEAD_DISTANCE;
    const halfWidth = PathFollower.CHARACTER_WIDTH / 2;
    const halfDepth = PathFollower.CHARACTER_DEPTH / 2;

    // Query box in front of character
    const aheadX = charX + dirX * lookAhead;
    const aheadZ = charZ + dirZ * lookAhead;

    const queryBox = {
      minX: Math.min(charX, aheadX) - halfWidth,
      maxX: Math.max(charX, aheadX) + halfWidth,
      minY: charY + 0.1,
      maxY: charY + PathFollower.CHARACTER_HEIGHT,
      minZ: Math.min(charZ, aheadZ) - halfDepth,
      maxZ: Math.max(charZ, aheadZ) + halfDepth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    if (obstacles.size === 0) return null;

    // Calculate avoidance direction
    let avoidX = 0;
    let avoidZ = 0;

    for (const obstacle of obstacles) {
      const obsTop = obstacle.y + obstacle.height;

      // Skip if this is our floor
      if (Math.abs(obsTop - charY) < 0.1) continue;
      if (obsTop <= charY + 0.1) continue;

      // Skip platforms/ceilings above us (base is above our feet level)
      // These don't block horizontal movement - only walls at our level do
      // Threshold of 2 units covers normal staircase step heights
      if (obstacle.y >= charY + 0.1) continue;

      // Calculate obstacle center
      const obsCenterX = obstacle.x + obstacle.width / 2;
      const obsCenterZ = obstacle.z + obstacle.depth / 2;

      // Direction away from obstacle
      const awayX = charX - obsCenterX;
      const awayZ = charZ - obsCenterZ;
      const awayLen = Math.sqrt(awayX * awayX + awayZ * awayZ);

      if (awayLen > 0) {
        // Weight by inverse distance
        const weight = 1 / (awayLen + 0.5);
        avoidX += (awayX / awayLen) * weight;
        avoidZ += (awayZ / awayLen) * weight;
      }
    }

    const avoidLen = Math.sqrt(avoidX * avoidX + avoidZ * avoidZ);
    if (avoidLen < 0.01) return null;

    return {
      x: avoidX / avoidLen,
      z: avoidZ / avoidLen
    };
  }

  /**
   * Clamp velocity to prevent walking off surface edges
   * @private
   */
  _clampVelocityToSurface(velX, velZ, deltaTime) {
    const charX = this.character.x;
    const charZ = this.character.z;
    const charY = this.character.y;

    // Find the floor we're standing on
    const floorQuery = {
      minX: charX - 0.1,
      maxX: charX + 0.1,
      minY: charY - 0.5,
      maxY: charY,
      minZ: charZ - 0.1,
      maxZ: charZ + 0.1
    };

    const floors = this.collisionSystem.queryCuboids(floorQuery);
    if (floors.size === 0) {
      return { x: velX, z: velZ };
    }

    // Find the floor closest to our feet
    let currentFloor = null;
    let closestDist = Infinity;
    for (const floor of floors) {
      const floorTop = floor.y + floor.height;
      const dist = Math.abs(floorTop - charY);
      if (dist < closestDist) {
        closestDist = dist;
        currentFloor = floor;
      }
    }

    if (!currentFloor) {
      return { x: velX, z: velZ };
    }

    // Get the current target waypoint to check if we need to allow edge approach
    const waypoint = this.getCurrentWaypoint();

    // Calculate surface bounds with edge margin
    const margin = PathFollower.EDGE_CLAMP_DISTANCE;
    const minX = currentFloor.x + margin;
    const maxX = currentFloor.x + currentFloor.width - margin;
    const minZ = currentFloor.z + margin;
    const maxZ = currentFloor.z + currentFloor.depth - margin;

    // Check if waypoint is outside the clamped bounds (near edge for transition)
    // If so, allow movement toward that edge
    let allowMinX = false, allowMaxX = false, allowMinZ = false, allowMaxZ = false;
    if (waypoint) {
      // Check if waypoint is outside floor bounds or at margin (transition point)
      const wpOnFloor = waypoint.x >= currentFloor.x && waypoint.x <= currentFloor.x + currentFloor.width &&
                        waypoint.z >= currentFloor.z && waypoint.z <= currentFloor.z + currentFloor.depth;

      if (wpOnFloor) {
        // Waypoint is on this floor - check if it's near an edge
        if (waypoint.x < minX) allowMinX = true;
        if (waypoint.x > maxX) allowMaxX = true;
        if (waypoint.z < minZ) allowMinZ = true;
        if (waypoint.z > maxZ) allowMaxZ = true;
      } else {
        // Waypoint is off this floor - it's a transition, allow movement toward it
        if (waypoint.x < currentFloor.x) allowMinX = true;
        if (waypoint.x > currentFloor.x + currentFloor.width) allowMaxX = true;
        if (waypoint.z < currentFloor.z) allowMinZ = true;
        if (waypoint.z > currentFloor.z + currentFloor.depth) allowMaxZ = true;
      }
    }

    // Calculate next position
    const nextX = charX + velX * deltaTime;
    const nextZ = charZ + velZ * deltaTime;

    let clampedVelX = velX;
    let clampedVelZ = velZ;

    // Check if there's ground under the next position (prevent corner cutting over gaps)
    const hasGroundAhead = this._hasGroundAt(nextX, charY, nextZ);

    if (!hasGroundAhead) {
      // Check if waypoint is intentionally across a gap (fall/jump transition)
      const isIntentionalGap = waypoint && (
        waypoint.type === 'fall' ||
        waypoint.type === 'jump' ||
        waypoint.y < charY - 0.3  // Waypoint is significantly below us
      );

      // Also check if the waypoint itself has ground - if so, trust the pathfinder
      // This handles corners where platforms meet at angles
      const waypointHasGround = waypoint && this._hasGroundAt(waypoint.x, waypoint.y, waypoint.z);

      if (!isIntentionalGap && !waypointHasGround) {
        // No ground ahead, not intentional, and waypoint has no ground - truly unsafe
        // Check if moving only in X or only in Z has ground
        const hasGroundX = this._hasGroundAt(nextX, charY, charZ);
        const hasGroundZ = this._hasGroundAt(charX, charY, nextZ);

        if (!hasGroundX && !hasGroundZ) {
          // No safe direction - stop movement
          Debug.log('PathFollower', 'NO GROUND: stopping at gap');
          clampedVelX = 0;
          clampedVelZ = 0;
        } else if (!hasGroundX) {
          // Only Z movement is safe
          Debug.log('PathFollower', 'NO GROUND X: clamping to Z only');
          clampedVelX = 0;
        } else if (!hasGroundZ) {
          // Only X movement is safe
          Debug.log('PathFollower', 'NO GROUND Z: clamping to X only');
          clampedVelZ = 0;
        }
      }
      // If waypoint has ground, trust the pathfinder and allow movement
    }

    // Clamp X velocity if we'd go past edge (unless allowed for transition)
    if (nextX < minX && velX < 0 && !allowMinX) {
      const allowedX = minX - charX;
      clampedVelX = Math.max(velX, allowedX / deltaTime);
      Debug.log('PathFollower', 'EDGE CLAMP X-: nextX=' + nextX.toFixed(2) + ' minX=' + minX.toFixed(2));
    } else if (nextX > maxX && velX > 0 && !allowMaxX) {
      const allowedX = maxX - charX;
      clampedVelX = Math.min(velX, allowedX / deltaTime);
      Debug.log('PathFollower', 'EDGE CLAMP X+: nextX=' + nextX.toFixed(2) + ' maxX=' + maxX.toFixed(2));
    }

    // Clamp Z velocity if we'd go past edge (unless allowed for transition)
    if (nextZ < minZ && velZ < 0 && !allowMinZ) {
      const allowedZ = minZ - charZ;
      clampedVelZ = Math.max(velZ, allowedZ / deltaTime);
      Debug.log('PathFollower', 'EDGE CLAMP Z-: nextZ=' + nextZ.toFixed(2) + ' minZ=' + minZ.toFixed(2));
    } else if (nextZ > maxZ && velZ > 0 && !allowMaxZ) {
      const allowedZ = maxZ - charZ;
      clampedVelZ = Math.min(velZ, allowedZ / deltaTime);
      Debug.log('PathFollower', 'EDGE CLAMP Z+: nextZ=' + nextZ.toFixed(2) + ' maxZ=' + maxZ.toFixed(2));
    }

    return { x: clampedVelX, z: clampedVelZ };
  }

  /**
   * Check if there's walkable ground at a position
   * @private
   */
  _hasGroundAt(x, y, z) {
    if (!this.collisionSystem) return true; // Assume ground if no collision system

    // Query for floor under position
    const groundQuery = {
      minX: x - 0.2,
      maxX: x + 0.2,
      minY: y - 1.0, // Look for floor up to 1 unit below
      maxY: y + 0.1,
      minZ: z - 0.2,
      maxZ: z + 0.2
    };

    const floors = this.collisionSystem.queryCuboids(groundQuery);

    // Check if any cuboid has its top near our Y (is a valid floor)
    for (const floor of floors) {
      const floorTop = floor.y + floor.height;
      // Floor top should be at or slightly below our feet
      if (floorTop <= y + 0.1 && floorTop >= y - 0.5) {
        return true;
      }
    }

    return false;
  }

  /**
   * Complete path following
   * @private
   */
  _completeFollowing() {
    this.isFollowing = false;
    this.path = null;
    this.waypointIndex = 0;
    this._stuckTimer = 0;
    this._smoothedDirX = 0;
    this._smoothedDirZ = 0;
    this._lastY = null;

    // Stop character movement
    if (this.character) {
      this.character.velocity.x = 0;
      this.character.velocity.z = 0;
      this.character.setMovementState(false, false);
    }

    // Trigger completion callback
    if (this.onComplete) {
      this.onComplete();
    }
  }

  /**
   * Get progress along current path (0-1)
   * @returns {number}
   */
  getProgress() {
    if (!this.path || this.path.length === 0) return 0;
    return this.waypointIndex / this.path.length;
  }

  /**
   * Get remaining distance to destination
   * @returns {number}
   */
  getRemainingDistance() {
    if (!this.isFollowing || !this.path) return 0;

    let distance = 0;
    let prevX = this.character.x;
    let prevZ = this.character.z;

    for (let i = this.waypointIndex; i < this.path.length; i++) {
      const wp = this.path[i];
      const dx = wp.x - prevX;
      const dz = wp.z - prevZ;
      distance += Math.sqrt(dx * dx + dz * dz);
      prevX = wp.x;
      prevZ = wp.z;
    }

    return distance;
  }

  /**
   * Get the current path
   * @returns {Array|null}
   */
  getPath() {
    return this.path;
  }

  /**
   * Get current waypoint index
   * @returns {number}
   */
  getWaypointIndex() {
    return this.waypointIndex;
  }
}
